import { useEffect, useState, useCallback, useMemo } from 'react';
import { FiCopy, FiCheck, FiShield, FiUser, FiClock, FiHash, FiCreditCard, FiCpu, FiArrowDownCircle, FiCheckCircle, FiKey, FiTrash2, FiMoreVertical, FiPlus, FiX, FiAlertCircle, FiArrowUpCircle, FiList, FiRefreshCw, FiDollarSign, FiLayers } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { getUserWallets, getWalletBalance, setTradingWallet, exportPrivateKey, deleteWallet, getWhitelist, addWhitelist, removeWhitelist } from '../utils/api';
import { formatUsdc } from '../utils/formatUtils';
import { DepositModal } from '../components/DepositModal';
import { PrivateKeyModal } from '../components/PrivateKeyModal';
import { WithdrawModal } from '../components/WithdrawModal';
import { WhitelistModal } from '../components/WhitelistModal';
import { PageLoading } from '../components/LoadingSpinner';
import { useI18n } from '../i18n/useI18n';

export default function WalletsPage({ pushToast }) {
  const { isAuthenticated, userId } = useAuth();
  const { t, lang } = useI18n();
  const locale = lang === 'zh' ? 'zh-CN' : 'en-US';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wallets, setWallets] = useState([]);
  const [whitelist, setWhitelist] = useState([]);
  const [copiedAddress, setCopiedAddress] = useState(null);
  const [depositModal, setDepositModal] = useState({ open: false, wallet: null });
  const [withdrawModal, setWithdrawModal] = useState({ open: false, wallet: null });
  const [pkModal, setPkModal] = useState({ open: false, privateKey: '' });
  const [whitelistModalOpen, setWhitelistModalOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const walletRes = await getUserWallets(userId);
      if (walletRes.data) {
        const walletsWithBalance = await Promise.all(walletRes.data.map(async (wallet) => {
          try {
            const balanceResp = await getWalletBalance(userId, wallet.wallet_address);
            const balance = balanceResp?.data || {};
            const totalUsdc = (balance.usdc ?? 0) + (balance.usdc_native ?? 0);
            
            return {
              ...wallet,
              user_id: userId,
              usdc_balance: balance.usdc ?? wallet.usdc_balance ?? 0,
              pol_balance: balance.pol ?? wallet.pol_balance ?? 0,
              usdc_native: balance.usdc_native ?? wallet.usdc_native,
              total_usdc: totalUsdc
            };
          } catch (balanceErr) {
            console.warn('获取钱包余额失败', balanceErr);
            return wallet;
          }
        }));

        const sorted = [...walletsWithBalance].sort((a, b) => {
          if (a.is_trading_wallet === b.is_trading_wallet) return 0;
          return a.is_trading_wallet ? -1 : 1;
        });
        setWallets(sorted);
      }

      const whitelistRes = await getWhitelist(userId);
      if (whitelistRes.data) {
        setWhitelist(whitelistRes.data);
      }

    } catch (err) {
      setError(err.message || t('wallets.fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [userId, t]);

  useEffect(() => {
    if (userId) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [userId, loadData]);

  useEffect(() => {
    const handleClickOutside = () => setMenuOpen(null);
    if (menuOpen) {
      document.addEventListener('click', handleClickOutside);
    }
    return () => document.removeEventListener('click', handleClickOutside);
  }, [menuOpen]);

  const stats = useMemo(() => {
    const totalUsdc = wallets.reduce((sum, w) => sum + (w.total_usdc || 0), 0);
    const totalPol = wallets.reduce((sum, w) => sum + (w.pol_balance || 0), 0);
    const safeCount = wallets.filter(w => w.wallet_type?.toLowerCase() === 'safe').length;
    const eoaCount = wallets.length - safeCount;
    const tradingWallet = wallets.find(w => w.is_trading_wallet);
    
    return {
      totalUsdc,
      totalPol,
      walletCount: wallets.length,
      safeCount,
      eoaCount,
      tradingWallet
    };
  }, [wallets]);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedAddress(text);
      pushToast(t('wallets.addressCopied'), 'success');
      setTimeout(() => setCopiedAddress(null), 2000);
    }).catch(() => {
      pushToast(t('wallets.copyFailed'), 'error');
    });
  };

  const handleSetTradingWallet = async (walletAddress) => {
    try {
      setLoading(true);
      await setTradingWallet(userId, walletAddress);
      pushToast(t('wallets.setTradingWalletOk'), 'success');
      await loadData();
    } catch (err) {
      pushToast(err.message || t('wallets.setFailed'), 'error');
      setLoading(false);
    }
  };

  const handleExportKey = async (wallet) => {
    setMenuOpen(null);
    try {
      const res = await exportPrivateKey(userId, wallet.wallet_address);
      if (res.code === 0 && res.data?.private_key) {
        setPkModal({ open: true, privateKey: res.data.private_key });
      } else {
        pushToast(res.msg || t('wallets.exportFailed'), 'error');
      }
    } catch (e) {
      pushToast(e.message || t('wallets.requestFailed'), 'error');
    }
  };

  const handleDeleteWallet = async (walletAddress) => {
    setMenuOpen(null);
    const walletToDelete = wallets.find(w => w.wallet_address === walletAddress);
    if (walletToDelete && walletToDelete.is_trading_wallet) {
      pushToast(t('wallets.tradingWalletCannotDelete'), 'error');
      return;
    }

    if (!window.confirm(t('wallets.deleteWalletConfirm'))) return;
    try {
      await deleteWallet(userId, walletAddress);
      pushToast(t('wallets.walletRemoved'), 'success');
      setWallets((prev) => prev.filter((w) => w.wallet_address !== walletAddress));
    } catch (e) {
      pushToast(t('wallets.removeFailed'), 'error');
    }
  };

  const handleAddWhitelist = async (address) => {
    if (!address.startsWith('0x') || address.length !== 42) {
      pushToast(t('wallets.invalidWalletAddress'), 'error');
      return;
    }
    
    try {
      const res = await addWhitelist(userId, address);
      if (res.code === 0) {
        pushToast(t('wallets.whitelistAdded'), 'success');
        setWhitelist(prev => [...prev, address.toLowerCase()]);
      } else {
        pushToast(res.msg || t('wallets.addFailed'), 'error');
      }
    } catch (e) {
      pushToast(e.message || t('wallets.addFailed'), 'error');
    }
  };

  const handleRemoveWhitelist = async (address) => {
    if (!window.confirm(t('wallets.removeWhitelistConfirm', { address }))) return;
    try {
      const res = await removeWhitelist(userId, address);
      if (res.code === 0) {
        pushToast(t('wallets.whitelistRemoved'), 'success');
        setWhitelist(prev => prev.filter(a => a !== address));
      } else {
        pushToast(res.msg || t('wallets.removeFailed'), 'error');
      }
    } catch (e) {
      pushToast(e.message || t('wallets.removeFailed'), 'error');
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    try {
      const date = new Date(isoString);
      return date.toLocaleString(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).replace(/\//g, '-');
    } catch {
      return isoString;
    }
  };

  if (!isAuthenticated || !userId) {
    return (
      <section className="glass-lg w-full min-h-[calc(100vh-14rem)] flex flex-col items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-slate-800/50 flex items-center justify-center">
            <FiCreditCard className="w-10 h-10 text-slate-500" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">{t('wallets.loginRequiredTitle')}</h2>
          <p className="text-slate-400">{t('wallets.loginRequiredDesc')}</p>
        </div>
      </section>
    );
  }

  if (loading && wallets.length === 0) {
    return <PageLoading text={t('wallets.loading')} />;
  }

  if (error) {
    return (
      <section className="glass-lg w-full min-h-[calc(100vh-14rem)] flex flex-col items-center justify-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-rose-500/10 flex items-center justify-center">
          <FiAlertCircle className="w-8 h-8 text-rose-400" />
        </div>
        <h2 className="text-xl font-bold text-rose-400 mb-2">{t('wallets.loadFailed')}</h2>
        <p className="text-slate-400 mb-6">{error}</p>
        <button 
          onClick={loadData} 
          className="px-6 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-medium transition-colors flex items-center gap-2"
        >
          <FiRefreshCw className="w-4 h-4" />
          {t('common.retry')}
        </button>
      </section>
    );
  }

  return (
    <section className="glass-lg w-full min-h-[calc(100vh-14rem)] flex flex-col">
      <div className="p-4 md:p-6 lg:p-8 flex flex-col gap-6 flex-1">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-slate-500 uppercase mb-2">
              {t('wallets.walletManagement')}
            </p>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl md:text-3xl font-bold text-white tracking-tight">
                {t('wallets.title')}
              </h1>
              <button 
                onClick={loadData} 
                className="p-2 rounded-xl bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all active:scale-95" 
                title={t('wallets.refreshTitle')}
              >
                <FiRefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
          
          <button
            onClick={() => setWhitelistModalOpen(true)}
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-800/80 hover:bg-slate-700 text-slate-200 hover:text-white rounded-xl font-medium transition-all border border-slate-700/50 hover:border-slate-600"
          >
            <FiList className="w-4 h-4" />
            {t('wallets.manageWhitelist')}
          </button>
        </div>

        {/* Stats Overview */}
        {wallets.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {/* Total USDC */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border border-emerald-500/20 p-4">
              <div className="absolute top-0 right-0 p-3 opacity-10">
                <FiDollarSign className="w-12 h-12 text-emerald-400" />
              </div>
              <p className="text-xs text-emerald-400/80 font-medium uppercase tracking-wider mb-1">{t('wallets.totalUsdc')}</p>
              <p className="text-xl md:text-2xl font-bold text-white">{formatUsdc(stats.totalUsdc)}</p>
              <p className="text-xs text-slate-500 mt-1">USDC</p>
            </div>

            {/* Total POL */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-500/10 to-indigo-600/5 border border-indigo-500/20 p-4">
              <div className="absolute top-0 right-0 p-3 opacity-10">
                <FiLayers className="w-12 h-12 text-indigo-400" />
              </div>
              <p className="text-xs text-indigo-400/80 font-medium uppercase tracking-wider mb-1">{t('wallets.totalPol')}</p>
              <p className="text-xl md:text-2xl font-bold text-white">{formatUsdc(stats.totalPol)}</p>
              <p className="text-xs text-slate-500 mt-1">POL</p>
            </div>

            {/* Wallet Count */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-sky-500/10 to-sky-600/5 border border-sky-500/20 p-4">
              <div className="absolute top-0 right-0 p-3 opacity-10">
                <FiCreditCard className="w-12 h-12 text-sky-400" />
              </div>
              <p className="text-xs text-sky-400/80 font-medium uppercase tracking-wider mb-1">{t('wallets.walletCount')}</p>
              <p className="text-xl md:text-2xl font-bold text-white">{stats.walletCount}</p>
              <p className="text-xs text-slate-500 mt-1">
                {stats.safeCount} Safe · {stats.eoaCount} EOA
              </p>
            </div>

            {/* Trading Wallet */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-500/10 to-amber-600/5 border border-amber-500/20 p-4">
              <div className="absolute top-0 right-0 p-3 opacity-10">
                <FiCheckCircle className="w-12 h-12 text-amber-400" />
              </div>
              <p className="text-xs text-amber-400/80 font-medium uppercase tracking-wider mb-1">{t('wallets.tradingWallet')}</p>
              <p className="text-lg font-bold text-white truncate">
                {stats.tradingWallet 
                  ? `${stats.tradingWallet.wallet_address?.slice(0, 6)}...${stats.tradingWallet.wallet_address?.slice(-4)}`
                  : t('wallets.notSet')
                }
              </p>
              <p className="text-xs text-slate-500 mt-1">
                {stats.tradingWallet ? t('wallets.active') : t('wallets.inactive')}
              </p>
            </div>
          </div>
        )}

        {/* Wallet List */}
        {wallets.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center py-20 rounded-2xl border-2 border-dashed border-slate-800 bg-slate-900/20">
            <div className="w-20 h-20 mb-6 rounded-2xl bg-slate-800/50 flex items-center justify-center">
              <FiCreditCard className="w-10 h-10 text-slate-600" />
            </div>
            <p className="text-slate-400 font-medium text-lg mb-2">{t('wallets.noWallets')}</p>
            <p className="text-slate-600 text-sm">{t('wallets.noWalletsHint')}</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider px-1">
              {t('wallets.walletList')} · {wallets.length}
            </p>
            
            {wallets.map((wallet, index) => {
              const isSafe = wallet.wallet_type?.toLowerCase() === 'safe';
              const displayAddress = isSafe ? wallet.safe_address : wallet.wallet_address;
              const shortLabel = displayAddress
                ? `${displayAddress.slice(0, 6)}...${displayAddress.slice(-4)}`
                : `${index + 1}`;
              const isSystemGenerated = wallet.source === 'system_generated';
              
              return (
                <div 
                  key={wallet.wallet_address}
                  className={`
                    group relative rounded-2xl border transition-all duration-300
                    ${wallet.is_trading_wallet 
                      ? 'bg-gradient-to-r from-sky-500/5 to-transparent border-sky-500/30 shadow-lg shadow-sky-500/5' 
                      : 'bg-slate-900/40 border-slate-700/50 hover:bg-slate-800/60 hover:border-slate-600/80'
                    }
                  `}
                >
                  {/* Trading Wallet Badge */}
                  {wallet.is_trading_wallet && (
                    <div className="absolute top-0 left-0 px-3 py-1 bg-sky-500 text-white text-[0.65rem] font-bold uppercase tracking-wider rounded-br-xl">
                      {t('wallets.mainWallet')}
                    </div>
                  )}
                  
                  <div className="p-4 md:p-5">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                      {/* Left: Icon & Info */}
                      <div className="flex items-start gap-4 flex-1 min-w-0">
                        {/* Icon */}
                        <div className={`
                          w-12 h-12 rounded-xl flex shrink-0 items-center justify-center text-xl
                          ${isSafe 
                            ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' 
                            : 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20'
                          }
                        `}>
                          {isSafe ? <FiShield /> : <FiUser />}
                        </div>
                        
                        {/* Info */}
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <h3 className="text-base font-bold text-slate-200">
                              {t('wallets.walletLabel', { label: shortLabel })}
                            </h3>
                            <div className="flex items-center gap-1.5">
                              <span className={`
                                text-[0.65rem] px-2 py-0.5 rounded-full font-medium
                                ${isSafe 
                                  ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' 
                                  : 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20'
                                }
                              `}>
                                {isSafe ? 'Safe' : 'EOA'}
                              </span>
                              <span className="text-[0.65rem] px-2 py-0.5 rounded-full bg-slate-700/50 text-slate-400 border border-slate-600/30">
                                {isSystemGenerated ? t('wallets.systemCreated') : t('wallets.userImported')}
                              </span>
                            </div>
                          </div>
                          
                          {/* Address */}
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-950/60 border border-slate-800 rounded-lg">
                              <code className="text-xs text-slate-400 font-mono">
                                {displayAddress?.slice(0, 10)}...{displayAddress?.slice(-8)}
                              </code>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleCopy(displayAddress); }}
                                className="text-slate-500 hover:text-sky-400 transition-colors p-1 hover:bg-slate-800 rounded"
                                title={t('common.copy')}
                              >
                                {copiedAddress === displayAddress ? <FiCheck size={14} className="text-emerald-400" /> : <FiCopy size={14} />}
                              </button>
                            </div>
                            <span className="text-xs text-slate-600">
                              {formatTime(wallet.created_at).split(' ')[0]}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Middle: Balances */}
                      <div className="flex items-center gap-6 lg:px-6 lg:border-x lg:border-slate-800/50">
                        <div className="text-center">
                          <p className="text-xs text-slate-500 mb-1">{t('wallets.usdcBalance')}</p>
                          <p className="text-lg font-bold text-slate-200">{formatUsdc(wallet.total_usdc)}</p>
                        </div>
                        {!isSafe && wallet.wallet_provider !== 'privy' && (
                          <div className="text-center">
                            <p className="text-xs text-slate-500 mb-1">{t('wallets.polBalance')}</p>
                            <p className="text-lg font-bold text-slate-200">{formatUsdc(wallet.pol_balance)}</p>
                          </div>
                        )}
                      </div>

                      {/* Right: Actions */}
                      <div className="flex items-center gap-2">
                        {!wallet.is_trading_wallet && (
                          <button
                            onClick={() => handleSetTradingWallet(wallet.wallet_address)}
                            className="flex items-center gap-1.5 px-3 py-2 bg-slate-800/50 hover:bg-sky-500/10 text-slate-400 hover:text-sky-400 border border-slate-700 hover:border-sky-500/30 rounded-xl transition-all text-xs font-medium"
                            title={t('wallets.setAsTradingWallet')}
                          >
                            <FiCheckCircle className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">{t('wallets.setAsPrimary')}</span>
                          </button>
                        )}
                        
                        <button 
                          onClick={() => setDepositModal({ 
                            open: true, 
                            wallet: wallet,
                            address: displayAddress,
                            name: t('wallets.walletLabel', { label: index + 1 }),
                            type: wallet.wallet_type
                          })}
                          className="flex items-center gap-1.5 px-3 py-2 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/20 hover:border-emerald-500/40 rounded-xl transition-all text-xs font-medium"
                        >
                          <FiArrowDownCircle className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">{t('wallets.deposit')}</span>
                        </button>

                        {isSystemGenerated && (
                          <button 
                            onClick={() => setWithdrawModal({ open: true, wallet: wallet })}
                            className="flex items-center gap-1.5 px-3 py-2 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/20 hover:border-rose-500/40 rounded-xl transition-all text-xs font-medium"
                          >
                            <FiArrowUpCircle className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">{t('wallets.withdraw')}</span>
                          </button>
                        )}
                        
                        {/* Menu */}
                        <div className="relative">
                          <button
                            onClick={(e) => { e.stopPropagation(); setMenuOpen(menuOpen === wallet.wallet_address ? null : wallet.wallet_address); }}
                            className="p-2 rounded-xl text-slate-500 hover:text-white hover:bg-slate-700/50 transition-colors"
                          >
                            <FiMoreVertical className="w-4 h-4" />
                          </button>
                          
                          {menuOpen === wallet.wallet_address && (
                            <div className="absolute right-0 mt-2 w-44 py-1.5 bg-slate-800 border border-slate-700 rounded-xl shadow-2xl z-[60] overflow-hidden">
                              {isSystemGenerated && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleExportKey(wallet); }}
                                  className="w-full px-4 py-2.5 text-left text-sm text-slate-300 hover:bg-slate-700 hover:text-white flex items-center gap-3 transition-colors"
                                >
                                  <FiKey className="w-4 h-4" />
                                  {t('privateKey.title')}
                                </button>
                              )}
                              <button
                                onClick={(e) => { e.stopPropagation(); handleDeleteWallet(wallet.wallet_address); }}
                                className="w-full px-4 py-2.5 text-left text-sm text-rose-400 hover:bg-rose-900/20 hover:text-rose-300 flex items-center gap-3 transition-colors"
                              >
                                <FiTrash2 className="w-4 h-4" />
                                {t('wallets.removeWallet')}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modals */}
      <DepositModal 
        open={depositModal.open} 
        onClose={() => setDepositModal({ open: false, wallet: null })} 
        walletAddress={depositModal.address}
        walletName={depositModal.name}
        walletType={depositModal.type}
        pushToast={pushToast}
      />
      
      <WithdrawModal 
        open={withdrawModal.open} 
        onClose={() => setWithdrawModal({ open: false, wallet: null })} 
        wallet={withdrawModal.wallet}
        whitelist={whitelist}
        pushToast={pushToast}
      />
      
      <PrivateKeyModal 
        open={pkModal.open} 
        onClose={() => setPkModal({ open: false, privateKey: '' })} 
        privateKey={pkModal.privateKey}
        pushToast={pushToast}
      />
      
      <WhitelistModal 
        open={whitelistModalOpen} 
        onClose={() => setWhitelistModalOpen(false)} 
        whitelist={whitelist}
        onAdd={handleAddWhitelist}
        onRemove={handleRemoveWhitelist}
        pushToast={pushToast}
      />
    </section>
  );
}
